import { useMemo } from 'react';
import {
  Link,
  LoaderFunction,
  Navigate,
  redirect,
  useLoaderData,
  useSearchParams,
} from 'react-router-dom';
import { paths } from '@mosey/api-types';
import { isUSStateCode, USStateId } from '@mosey/utils/constants/us-states';
import { TruncatedList } from '@mosey/components/layout/TruncatedList';
import { TaskCollectionCard } from '@mosey/components/layout/TaskCollectionCard';
import { TaskSummaryCard } from '@mosey/components/layout/Card';
import { apiBatch } from '../../../utils/fetchApi';
import { TaskRef } from '../utils/types';
import { getTaskDueDate, isTaskOverdue } from '../utils';
import { TasksOverviewZeroState } from './TasksOverviewZeroState';
import { useUser } from '../../../hooks/useUser';
import { User } from '../../../types';
import { TasksOverviewIntegrations } from './TasksOverviewIntegrations';
import { TaskOverviewLocationFilters } from './TaskOverviewLocationFilters';

export const loader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url);
  const locationId = url.searchParams.get('location_id');
  const searchParams = new URLSearchParams();
  // TODO: Make task list URL builder function
  let tasksUrl = '/api/compliance/tasks';

  if (locationId) {
    const code = locationId.toUpperCase();

    if (!isUSStateCode(code)) {
      return redirect(`/home`);
    }

    searchParams.set('region_id', USStateId[code]);
  }

  if (searchParams.size > 0) {
    tasksUrl = `${tasksUrl}?${searchParams.toString()}`;
  }

  return apiBatch({
    tasks: {
      url: tasksUrl,
      method: 'GET',
    },
    legislation: {
      url: '/api/legislation',
      method: 'GET',
    },
    connections: {
      url: `/api/connections`,
      method: 'GET',
    },
  });
};

type Region = User['legal_entity']['regions'][0]['region'];
type Legislation =
  paths['/api/legislation']['get']['responses']['200']['content']['application/json'];
type Connections =
  paths['/api/connections']['get']['responses']['200']['content']['application/json'];

export const Component = () => {
  const {
    role,
    legal_entity: { is_gutz_enabled: isGutzEnabled, regions },
  } = useUser();
  const { tasks, legislation, connections } = useLoaderData() as {
    tasks: TaskRef[];
    legislation: Legislation;
    connections: Connections;
  };
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('location_id');
  const hasPayrollConnection = connections.some(
    ({ integration: { type } }) => type === 'payroll',
  );

  const {
    todos,
    inProgressTasks,
    assessmentAlerts,
    assessmentTasks,
    setupAlerts,
    setupTasks,
  } = useMemo(() => {
    const todos: TaskRef[] = [];
    const inProgressTasks: TaskRef[] = [];
    const setupTasksByRegion: Record<
      string,
      {
        assessment: TaskRef[];
        setup: TaskRef[];
      }
    > = {};

    const regionMap = regions.reduce(
      (acc, { region }) => {
        return { ...acc, [region.code]: region };
      },
      {} as Record<string, Region>,
    );

    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];

      if (task.status === 'in-progress') {
        inProgressTasks.push(task);
      } else if (task.source.is_setup_related) {
        if (task.source.type === 'requirement') {
          const regionCode = task.source.location.code;

          if (!setupTasksByRegion[regionCode]) {
            setupTasksByRegion[regionCode] = {
              assessment: [],
              setup: [],
            };
          }

          setupTasksByRegion[regionCode].setup.push(task);
        } else {
          const regionCode = task.source.locations[0].code;

          if (!setupTasksByRegion[regionCode]) {
            setupTasksByRegion[regionCode] = {
              assessment: [],
              setup: [],
            };
          }

          setupTasksByRegion[regionCode].assessment.push(task);
        }
      }
    }

    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];

      if (
        !task.source.is_setup_related &&
        task.source.type === 'requirement' &&
        setupTasksByRegion[task.source.location.code]?.assessment.every(
          (task) => task.status === 'done',
        ) &&
        setupTasksByRegion[task.source.location.code]?.setup.every(
          (task) => task.status === 'done',
        )
      ) {
        todos.push(task);
      }
    }

    const setupTasksByRegionList = Object.entries(setupTasksByRegion);

    const assessmentAlerts = setupTasksByRegionList
      .reduce((acc, [code, { assessment }]) => {
        if (assessment.length > 0) {
          return acc.concat(regionMap[code]);
        }

        return acc;
      }, [] as Region[])
      .sort((a, b) => a.name.localeCompare(b.name));

    const setupAlerts = setupTasksByRegionList
      .reduce((acc, [code, { assessment, setup }]) => {
        if (
          assessment.every((task) => task.status === 'done') &&
          setup.length > 0
        ) {
          return acc.concat(regionMap[code]);
        }

        return acc;
      }, [] as Region[])
      .sort((a, b) => a.name.localeCompare(b.name));

    const assessmentTasks = setupTasksByRegionList.flatMap(
      ([, { assessment }]) => assessment,
    );

    const setupTasks = setupTasksByRegionList.flatMap(
      ([, { assessment, setup }]) => {
        if (assessment.every((task) => task.status === 'done')) {
          return setup;
        }

        return [];
      },
    );

    return {
      todos,
      inProgressTasks,
      assessmentAlerts,
      assessmentTasks,
      setupAlerts,
      setupTasks,
    };
  }, [tasks, regions]);

  const filteredLegislation = legislation.filter(({ region }) => {
    if (locationId && region.code.toLowerCase() !== locationId) {
      return false;
    }

    return true;
  });

  if (role !== 'admin' || !isGutzEnabled) {
    return <Navigate to="/home" />;
  }

  return (
    <div className="w-full max-w-full">
      <TaskOverviewLocationFilters />

      <div className="grid grid-cols-[minmax(0,_1fr)_374px] items-start gap-x-10 px-8 pb-6">
        <div className="space-y-6">
          {todos.length > 0 ? (
            <ul className="space-y-2">
              {todos.map((task) => {
                const { id, status, title, source } = task;
                const formattedDueDate = getTaskDueDate(task);

                return (
                  <TaskSummaryCard
                    as="li"
                    key={id}
                    status={isTaskOverdue(task) ? 'overdue' : status}
                    title={title}
                    description={
                      source.type === 'requirement'
                        ? source.description
                        : undefined
                    }
                    reason={
                      formattedDueDate
                        ? { title: formattedDueDate, type: 'due' }
                        : undefined
                    }
                    // TODO: Update this when we have filters
                    to={`/resolver/${source.type === 'question' ? 'question' : 'requirement'}/tasks/${id}`}
                    region={
                      source.type === 'requirement'
                        ? source.location.name
                        : source.locations[0].name
                    }
                  />
                );
              })}
            </ul>
          ) : (
            <TasksOverviewZeroState />
          )}
        </div>

        <aside className="isolate space-y-7">
          {!hasPayrollConnection && <TasksOverviewIntegrations />}

          <TruncatedList
            title="Complete Setup"
            body="Setup tasks are based on your responses to questions"
            itemLabel={locationId ? 'tasks' : 'locations'}
          >
            {locationId
              ? setupTasks.map(({ id, title, status }) => (
                  <TaskSummaryCard
                    key={id}
                    as="li"
                    title={title}
                    status={status}
                    variant="nav"
                    to={`/locations/usa/${locationId}/resolver/setup/tasks/${id}`}
                  />
                ))
              : setupAlerts.map((region) => (
                  <TaskCollectionCard
                    key={region.code}
                    as="li"
                    title={region.name}
                    status="setup-collection"
                    size="medium"
                    to={`/locations/usa/${region.code.toLowerCase()}/resolver/setup`}
                    ariaLabelTitle={`${region.name} setup`}
                  />
                ))}
          </TruncatedList>

          <TruncatedList
            title="Answer Questions"
            body="Your responses generate setup tasks"
            itemLabel={locationId ? 'tasks' : 'locations'}
          >
            {locationId
              ? assessmentTasks.map(({ id, title, status }) => {
                  return (
                    <TaskSummaryCard
                      key={id}
                      as="li"
                      title={title}
                      status={status}
                      variant="nav"
                      to={`/locations/usa/${locationId}/resolver/assessment/tasks/${id}`}
                    />
                  );
                })
              : assessmentAlerts.map((region) => {
                  return (
                    <TaskCollectionCard
                      key={region.code}
                      as="li"
                      title={region.name}
                      status="question-collection"
                      size="medium"
                      to={`/locations/usa/${region.code.toLowerCase()}/resolver/assessment`}
                      ariaLabelTitle={`${region.name} assessment`}
                    />
                  );
                })}
          </TruncatedList>

          <TruncatedList
            title="Automations in Progress"
            body="You will be notified if action is required"
            itemLabel="automations"
            isStacked
          >
            {inProgressTasks.map(({ id, title, status }) => {
              return (
                <TaskSummaryCard
                  key={id}
                  as="li"
                  title={title}
                  status={status}
                  variant="nav"
                  to={`/tasks/${id}`}
                />
              );
            })}
          </TruncatedList>

          <TruncatedList
            title="Legislation"
            body="Tasks will be generated for legislation that applies to you"
            itemLabel="updates"
            isStacked
          >
            {filteredLegislation.map(
              ({ id, title, effective_date: effectiveDate }) => {
                return (
                  <Link
                    key={id}
                    className="relative z-0 -mt-px block space-y-0.5 border border-gray-200 px-3 py-[10px] text-sm outline-none last:border-b hover:z-10 hover:border-b hover:border-teal-300 hover:bg-teal-200 focus-visible:z-20 focus-visible:ring-2 focus-visible:ring-rose-700"
                    to={`/legislation#${id}`}
                  >
                    <p className="font-medium">{title}</p>
                    {effectiveDate && <p>Effective {effectiveDate}</p>}
                  </Link>
                );
              },
            )}
          </TruncatedList>

          {hasPayrollConnection && <TasksOverviewIntegrations />}
        </aside>
      </div>
    </div>
  );
};
